import type { Theme } from "@mui/material/styles";
import type { AppSx } from "../types";
import { buildFormLayoutStyles } from "../layout/formLayout";

export interface ChartOptionPanelsComponentStyles {
  root: AppSx;
  headerBlock: AppSx;
  title: AppSx;
  subtitle: AppSx;
  tabs: AppSx;
  tabLabelBox: AppSx;
  tabChip: AppSx;
  panelBody: AppSx;
  divider: AppSx;
  presetChipsRow: (showCustom: boolean) => AppSx;
  presetChip: AppSx;
  customGrid: AppSx;
  sideLabel: AppSx;
  sideValue: AppSx;
  slider: AppSx;
  fieldShellControl: (centerControl: boolean) => AppSx;
  fieldShellFormControl: AppSx;
  fieldShellControlBox: (centerControl: boolean) => AppSx;
  helperText: AppSx;
  orientationLabel: AppSx;
}

export function buildChartOptionPanelsComponentStyles(theme: Theme): ChartOptionPanelsComponentStyles {
  const form = buildFormLayoutStyles(theme);
  const fieldShellControlBox = (centerControl: boolean): AppSx => ({
    ...form.chartOptionFieldControlBox,
    alignItems: centerControl ? "center" : "stretch",
  });

  return {
    root: {
      mt: 1.5,
      border: "1px solid",
      borderColor: "divider",
      borderRadius: 1.5,
      overflow: "hidden",
      bgcolor: "background.paper",
    },
    headerBlock: { px: 1.5, pt: 1.25, pb: 0.25 },
    title: { fontSize: 13 },
    subtitle: { display: "block" },
    tabs: {
      minHeight: 36,
      mx: 1,
      mt: 0.75,
      mb: 0.25,
      width: "fit-content",
      maxWidth: "calc(100% - 16px)",
      "& .MuiTab-root": {
        minHeight: 36,
        py: 0.5,
        fontSize: 12,
        minWidth: "auto",
        px: 1.25,
      },
      "& .Mui-selected .MuiChip-root": {
        bgcolor: "rgba(255,255,255,0.22)",
        color: "inherit",
      },
    },
    tabLabelBox: { display: "inline-flex", alignItems: "center", gap: 0.75 },
    tabChip: {
      height: 18,
      fontSize: 10,
      fontWeight: 700,
      "& .MuiChip-label": { px: 0.75 },
    },
    panelBody: { p: 2 },
    divider: { my: 1.5 },
    presetChipsRow: (showCustom) => ({ mb: showCustom ? 1 : 0 }),
    presetChip: { fontWeight: 600 },
    customGrid: { mt: 0.5, "& > .MuiGrid-root": { display: "flex" } },
    sideLabel: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      fontSize: "0.75rem",
      fontWeight: 600,
      color: "text.secondary",
      mb: 0.25,
    },
    sideValue: { fontWeight: 700, color: "text.primary" },
    slider: { mt: 0.25, mb: 0.25 },
    fieldShellControl: (centerControl) => ({
      alignItems: centerControl ? "center" : "stretch",
    }),
    fieldShellFormControl: { ...form.chartOptionInput, height: "100%" },
    fieldShellControlBox,
    helperText: { mx: 0, mt: 0.25 },
    orientationLabel: {
      fontSize: "0.75rem",
      fontWeight: 600,
      mb: 0.25,
      color: "text.secondary",
    },
  };
}

export interface ChartEditorPanelComponentStyles {
  listItem: AppSx;
  listIcon: AppSx;
  startAdornmentBox: AppSx;
  familyHint: AppSx;
}

export function buildChartEditorPanelComponentStyles(_theme: Theme): ChartEditorPanelComponentStyles {
  return {
    listItem: { gap: 1.5, py: 1, px: 1.5 },
    listIcon: { minWidth: 34 },
    startAdornmentBox: { display: "flex", pl: 0.5, pr: 0.5 },
    familyHint: { mt: 1.5, display: "block" },
  };
}
